import { useState, useEffect, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth, api } from './context/AuthContext'
import { SocketProvider, useSocket } from './context/SocketContext'

import Login       from './pages/Login'
import Register      from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import Dashboard   from './pages/Dashboard'
import Products    from './pages/Products'
import Orders      from './pages/Orders'
import Analytics   from './pages/Analytics'
import Earnings    from './pages/Earnings'
import LiveHistory from './pages/LiveHistory'
import ShopSettings from './pages/ShopSettings'
import Reels        from './pages/Reels'
import Reports      from './pages/Reports'

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#060D1F',
  navyMid:    '#0D1835',
  navyLine:   '#111D36',
  orange:     '#E8440A',
  orangeHl:   'rgba(232,68,10,0.14)',
  white10:    'rgba(255,255,255,0.10)',
  white35:    'rgba(255,255,255,0.35)',
  white55:    'rgba(255,255,255,0.55)',
  white80:    'rgba(255,255,255,0.80)',
  bg:         '#F6F6F3',
  surface:    '#FFFFFF',
  border:     '#E9E9E4',
  text:       '#111317',
  muted:      '#9A9A94',
}

// ── Nav items ─────────────────────────────────────────────────────────────────
const NAV = [
  { id: 'dashboard',    label: 'Dashboard',     icon: '⊞',  path: '/dashboard'    },
  { id: 'products',     label: 'My Products',   icon: '📦', path: '/products'     },
  { id: 'orders',       label: 'Orders',        icon: '🛍', path: '/orders'       },
  { id: 'analytics',   label: 'Analytics',     icon: '📊', path: '/analytics'    },
  { id: 'earnings',     label: 'Earnings',      icon: '💰', path: '/earnings'     },
  { id: 'live-history', label: 'Live History',  icon: '🔴', path: '/live-history' },
  { id: 'reels',        label: 'Reels',         icon: '🎬', path: '/reels'        },
  { id: 'reports',      label: 'Reports',       icon: '📋', path: '/reports'      },
  { id: 'shop',         label: 'Shop Settings', icon: '🏪', path: '/shop'         },
]

// ── Toast notification ────────────────────────────────────────────────────────
function Toast({ notif, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000)
    return () => clearTimeout(t)
  }, [onDismiss])

  const isOrder = notif?.type === 'new_order' || notif?.type === 'order_status'
  const isChat  = notif?.type === 'delivery_chat'
  const iconBg  = isChat ? 'rgba(59,130,246,0.1)' : isOrder ? 'rgba(232,68,10,0.1)' : 'rgba(21,128,61,0.1)'
  const icon    = isChat ? '💬' : isOrder ? '🛒' : '🔔'
  return (
    <div onClick={onDismiss}
      style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, maxWidth: 340, background: '#fff',
        border: `1px solid ${isChat ? '#bfdbfe' : '#e9e9e4'}`, borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer',
        animation: 'toastIn 0.22s ease', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111317', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {notif?.title || (isOrder ? 'New Order!' : 'Notification')}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
          {notif?.message || ''}
        </div>
      </div>
      <div style={{ fontSize: 16, color: '#9a9a94', lineHeight: 1, flexShrink: 0 }}>×</div>
    </div>
  )
}

// ── Mobile hook ───────────────────────────────────────────────────────────────
function useMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return isMobile
}

// ── Sidebar nav item ──────────────────────────────────────────────────────────
function NavItem({ item, active, badge, chatDot, onNavigate }) {
  const navigate = useNavigate()
  const [hov, setHov] = useState(false)
  return (
    <div onClick={() => { navigate(item.path); onNavigate?.() }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px', cursor: 'pointer', fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? '#fff' : hov ? C.white80 : C.white55,
        background: active ? C.orangeHl : hov ? C.white10 : 'transparent',
        borderLeft: `2px solid ${active ? C.orange : 'transparent'}`,
        transition: 'all 0.14s', userSelect: 'none',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>
      <span style={{ fontSize: 15, width: 20, opacity: active ? 1 : 0.75, position: 'relative' }}>
        {item.icon}
        {/* Chat unread dot on icon */}
        {chatDot && (
          <span style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%',
            background: '#3b82f6', border: '1.5px solid #060D1F', display: 'block' }} />
        )}
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {badge > 0 && (
        <span style={{ background: C.orange, color: '#fff', fontSize: 9, fontWeight: 800,
          fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: 3, lineHeight: 1.4 }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </div>
  )
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ seller, onLogout, open, onClose, isMobile }) {
  const location   = useLocation()
  const activePath = location.pathname
  const { unreadCount, clearUnread, connected, pendingCount, chatUnread } = useSocket()

  if (isMobile && !open) return null

  const avatarBg = (str = '') => {
    const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff']
    let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
    return colors[Math.abs(h) % colors.length]
  }

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobile && open && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 99 }} />
      )}

      <aside style={{ width: 240, flexShrink: 0, background: C.navy, display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, overflowY: 'auto', zIndex: 100,
        fontFamily: "'Inter', 'Plus Jakarta Sans', sans-serif",
        transform: isMobile && !open ? 'translateX(-260px)' : 'translateX(0)',
        transition: 'transform 0.22s ease' }}>

        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${C.navyLine}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/kudos_logo.png" alt="Kudos Live" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', boxShadow: '0 4px 14px rgba(232,68,10,0.30)', flexShrink: 0 }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(145deg, ${C.orange} 0%, #c9380a 100%)`,
            display: 'none', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(232,68,10,0.30)', flexShrink: 0 }}>
            <span style={{ fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: '-0.04em' }}>K</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 }}>Kudos Live</div>
            <div style={{ fontSize: 10, color: C.white35, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, fontWeight: 500 }}>Seller Portal</div>
          </div>
          {/* Live indicator */}
          <div title={connected ? 'Live updates connected' : 'Connecting…'}
            style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? '#16a34a' : '#f59e0b',
              boxShadow: connected ? '0 0 0 2px rgba(22,163,74,0.3)' : '0 0 0 2px rgba(245,158,11,0.3)',
              flexShrink: 0, transition: 'background 0.3s' }} />
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 0', overflowY: 'auto' }}>
          {NAV.map(item => (
            <NavItem
              key={item.id}
              item={item}
              active={activePath === item.path || activePath.startsWith(item.path + '/')}
              badge={item.id === 'orders' ? pendingCount : 0}
              chatDot={item.id === 'orders' && chatUnread > 0}
              onNavigate={isMobile ? onClose : undefined}
            />
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.navyLine}`, display: 'flex', alignItems: 'center', gap: 10, background: C.navyMid }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: avatarBg(seller?.username || ''),
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: C.text }}>
            {(seller?.username || 'S').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {seller?.username || 'Seller'}
            </div>
            <div style={{ fontSize: 10, color: C.white35, fontFamily: "'JetBrains Mono', monospace", marginTop: 1 }}>
              {seller?.role || 'seller'}
            </div>
          </div>
          <div onClick={onLogout} title="Log out"
            style={{ color: C.white35, cursor: 'pointer', fontSize: 15, padding: 4, borderRadius: 6, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = C.white35}>↩</div>
        </div>
      </aside>
    </>
  )
}

// ── Topbar ────────────────────────────────────────────────────────────────────
function Topbar({ onMenuClick, isMobile }) {
  const location = useLocation()
  const current  = NAV.find(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
  return (
    <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`,
      padding: isMobile ? '0 16px' : '0 32px', height: 54,
      display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 50 }}>
      {isMobile && (
        <button onClick={onMenuClick}
          style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18,
            flexShrink: 0, color: C.text }}>
          ☰
        </button>
      )}
      <span style={{ fontSize: 12, color: C.muted }}>Seller</span>
      <span style={{ color: C.border }}>/</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{current?.label || 'Dashboard'}</span>
    </div>
  )
}

// ── Shell ─────────────────────────────────────────────────────────────────────
function Shell({ children }) {
  const { seller, logout }               = useAuth()
  const { notifications, clearUnread, on, clearChatUnread } = useSocket()
  const location                         = useLocation()
  const [toast, setToast]                = useState(null)
  const [sidebarOpen, setSidebarOpen]    = useState(false)
  const isMobile                         = useMobile()
  const dismissToast = useCallback(() => setToast(null), [])

  // Show toast for order notifications
  useEffect(() => {
    const unsub = on('notification', (notif) => {
      setToast(notif)
    })
    return unsub
  }, [on])

  // Show toast for incoming chat messages
  useEffect(() => {
    const unsub = on('delivery:message', (msg) => {
      setToast({
        type:    'delivery_chat',
        title:   `💬 Message from ${msg.username || (msg.role === 'driver' ? 'Driver' : 'Buyer')}`,
        message: msg.text?.length > 60 ? msg.text.slice(0, 58) + '…' : msg.text,
      })
    })
    return unsub
  }, [on])

  // Clear badges when navigating to /orders
  useEffect(() => {
    if (location.pathname === '/orders') { clearUnread(); clearChatUnread() }
  }, [location.pathname, clearUnread, clearChatUnread])

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Sidebar
        seller={seller}
        onLogout={logout}
        isMobile={isMobile}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div style={{ marginLeft: isMobile ? 0 : 240, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0 }}>
        <Topbar isMobile={isMobile} onMenuClick={() => setSidebarOpen(o => !o)} />
        <div style={{ flex: 1 }}>{children}</div>
        <div style={{ padding: '14px 24px', textAlign: 'center', fontSize: 11, color: C.muted }}>
          © {new Date().getFullYear()} Honor Creations Ltd. All rights reserved.
        </div>
      </div>
      {toast && <Toast notif={toast} onDismiss={dismissToast} />}
    </div>
  )
}

// ── Cross-app SSO handoff ────────────────────────────────────────────────────
// Picks up ?handoff=<code> from a link sent over from kudoslive.rw (a
// different origin, so nothing about that session — localStorage, the JWT
// header — carries over here on its own). Trades the code for a real JWT via
// a POST body (never a GET/URL, so it never ends up in address-bar history),
// then strips the param from the URL either way so the one-time code never
// lingers where it could be re-shared or re-visited.
const SplashLoading = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F6F6F3', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
    <div style={{ textAlign: 'center' }}>
      <img src="/kudos_logo.png" alt="Kudos Live" style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', margin: '0 auto 14px', display: 'block' }}
        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
      <div style={{ width: 38, height: 38, borderRadius: 10, background: '#E8440A', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 900, color: '#fff', margin: '0 auto 14px' }}>K</div>
      <div style={{ fontSize: 13, color: '#9A9A94' }}>Signing you in…</div>
    </div>
  </div>
)

function useHandoff() {
  const { login } = useAuth()
  const location   = useLocation()
  const navigate   = useNavigate()
  const [pending, setPending] = useState(() => new URLSearchParams(location.search).has('handoff'))

  useEffect(() => {
    const code = new URLSearchParams(location.search).get('handoff')
    if (!code) return
    let cancelled = false
    api.post('/user/handoff/exchange', { code })
      .then(res => { if (!cancelled && res.data?.token) login(res.data.token) })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return
        navigate(location.pathname, { replace: true }) // drop ?handoff= from the URL
        setPending(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return pending
}

// ── Route guards ──────────────────────────────────────────────────────────────
function Private({ children }) {
  const { seller, isLoading } = useAuth()
  if (isLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <img src="/kudos_logo.png" alt="Kudos Live" style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', margin: '0 auto 14px', display: 'block' }}
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
        <div style={{ width: 38, height: 38, borderRadius: 10, background: C.orange, display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 900, color: '#fff', margin: '0 auto 14px' }}>K</div>
        <div style={{ fontSize: 13, color: C.muted }}>Loading…</div>
      </div>
    </div>
  )
  if (!seller) return <Navigate to="/login" replace />
  return children
}

function Public({ children }) {
  const { seller, isLoading } = useAuth()
  if (isLoading) return null
  if (seller) return <Navigate to="/dashboard" replace />
  return children
}

// ── Router ────────────────────────────────────────────────────────────────────
function AppRouter() {
  const handoffPending = useHandoff()
  if (handoffPending) return <SplashLoading />
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login"           element={<Public><Login          /></Public>} />
      <Route path="/register"        element={<Public><Register       /></Public>} />
      <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
      <Route path="/dashboard"    element={<Private><Shell><Dashboard   /></Shell></Private>} />
      <Route path="/products"     element={<Private><Shell><Products    /></Shell></Private>} />
      <Route path="/orders"       element={<Private><Shell><Orders      /></Shell></Private>} />
      <Route path="/analytics"    element={<Private><Shell><Analytics   /></Shell></Private>} />
      <Route path="/earnings"     element={<Private><Shell><Earnings    /></Shell></Private>} />
      <Route path="/live-history" element={<Private><Shell><LiveHistory /></Shell></Private>} />
      <Route path="/Reels"        element={<Private><Shell><Reels       /></Shell></Private>} />
      <Route path="/reports"      element={<Private><Shell><Reports     /></Shell></Private>} />
      <Route path="/shop"         element={<Private><Shell><ShopSettings /></Shell></Private>} />
      <Route path="*"             element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SocketProvider>
          <AppRouter />
        </SocketProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}
